import axios from 'axios';
import {
	setUser,
	setLoading,
	setError,
	setOrganization,
	setNetworkStatus,
} from '../features/common/headerSlice';
import store from './store';

// Use sessionStorage for token to improve security (XSS protection through automatic expiry)
// but still maintain ability to login across tabs using localStorage with an expiry check
const getToken = () => {
	// First try sessionStorage (current tab)
	const sessionToken = sessionStorage.getItem('tokenV3');
	if (sessionToken) return sessionToken;

	// Then try localStorage (cross-tab persistence)
	const localToken = localStorage.getItem('tokenV3');
	const tokenExpiry = localStorage.getItem('tokenExpiryV2');

	// If we have a token and it's not expired, use it
	if (localToken && tokenExpiry) {
		const currentTime = new Date().getTime();
		const expiryTime = parseInt(tokenExpiry);

		if (currentTime < expiryTime) {
			// Valid token - copy to sessionStorage
			sessionStorage.setItem('tokenV3', localToken);
			return localToken;
		} else {
			// Token expired - clean up
			localStorage.removeItem('tokenV3');
			localStorage.removeItem('tokenExpiryV2');
			return null;
		}
	}

	return null;
};

const setToken = (token) => {
	// Always store in sessionStorage for current tab
	sessionStorage.setItem('tokenV3', token);

	// Also store in localStorage with an expiry (30 days)
	localStorage.setItem('tokenV3', token);

	// Set expiry 30 days from now
	const expiryTime = new Date().getTime() + 30 * 24 * 60 * 60 * 1000;
	localStorage.setItem('tokenExpiryV2', expiryTime.toString());
};

const clearToken = () => {
	sessionStorage.removeItem('tokenV3');
	localStorage.removeItem('tokenV3');
	localStorage.removeItem('tokenExpiryV2');
};

// Network health monitoring
let networkHealthy = true;
const MAX_RETRIES = 2;

const checkAuth = () => {
	const TOKEN = getToken();

	const PUBLIC_ROUTES = [
		'login',
		'activate-account',
		'forgot-password',
		'reset-password',
		'registration',
		'signup',
	];

	const isPublicPage = PUBLIC_ROUTES.some((r) => {
		return window.location.href.includes(r);
	});

	if (!TOKEN && !isPublicPage) {
		window.location.href = '/login';
		return;
	}

	// Set the base URL based on the current window location
	if (['local', 'development'].includes(import.meta.env.VITE_API_ENVIRONMENT)) {
		axios.defaults.baseURL = `http://${import.meta.env.VITE_API_URL}`;
	} else {
		const apiUrl = import.meta.env.VITE_API_URL;
		const urlSuffix = window.location.hostname.endsWith('.app')
			? '.app'
			: '.com';
		axios.defaults.baseURL = `https://${apiUrl.replace(/\.com$/, urlSuffix)}`;
	}

	// Only set auth header if token exists
	if (TOKEN) {
		axios.defaults.headers.common['Authorization'] = `Bearer ${TOKEN}`;
	} else {
		console.log('No token available, not setting Authorization header');
	}

	// Determine if error is retryable
	const isRetryableError = (error) => {
		return (
			error.code === 'ERR_NETWORK' ||
			error.message === 'Network Error' ||
			(error.response && error.response.status >= 500)
		);
	};

	// Determine if error is a genuine network connectivity issue
	const isNetworkConnectivityError = (error) => {
		return error.code === 'ERR_NETWORK' || error.message === 'Network Error';
	};

	axios.interceptors.request.use(
		function (config) {
			// Don't set retry count on auth-related endpoints to prevent security issues
			if (
				!config.url.includes('/api/me') &&
				!config.url.includes('/api/login')
			) {
				// Initialize retry count if not already set
				config.retryCount = config.retryCount || 0;
			}

			const whiteList = [
				new RegExp(/^\/api\/chatGroupsMessage\/\d+$/),
				new RegExp(/^\/api\/notifications\?user=\d+$/),
				new RegExp(/^\/api\/chats\/\d+$/),
				new RegExp(/^\/api\/upload\/chunk$/),
				new RegExp(/^\/api\/upload$/),
				new RegExp(/^\/api\/settings$/),
				new RegExp(/^\/api\/workoutreader$/),
				new RegExp(/^\/api\/reporting\/organization\/([^/]+)\/llm$/),
			];

			let showLoadingModal = true;
			for (const rgx of whiteList) {
				if (rgx.test(config.url)) {
					showLoadingModal = false;
					break;
				}
			}

			// Skip global loading if specifically requested (e.g., for AI assistant)
			if (config.skipGlobalLoading) {
				showLoadingModal = false;
			}

			if (showLoadingModal) {
				document.body.classList.add('loading-indicator');
			}

			return config;
		},
		function (error) {
			return Promise.reject(error);
		}
	);

	axios.interceptors.response.use(
		function (response) {
			// Hide global loading indicator
			document.body.classList.remove('loading-indicator');

			// Reset network health on successful responses
			if (!networkHealthy) {
				networkHealthy = true;
				store.dispatch(setNetworkStatus({ isOnline: true }));
			}

			return response;
		},
		function (error) {
			document.body.classList.remove('loading-indicator');

			// Don't retry auth endpoints to prevent security issues
			const isAuthEndpoint =
				error.config &&
				(error.config.url.includes('/api/me') ||
					error.config.url.includes('/api/login'));

			// Implement retry logic for non-auth network errors
			if (!isAuthEndpoint && isRetryableError(error)) {
				const config = error.config || {};

				// Only update network status for actual connectivity issues, not server errors
				if (isNetworkConnectivityError(error)) {
					networkHealthy = false;
					store.dispatch(setNetworkStatus({ isOnline: false }));
				}

				// Only retry if we haven't exceeded max retries
				if (config.retryCount < MAX_RETRIES) {
					// Increase retry count
					config.retryCount = (config.retryCount || 0) + 1;

					// Create a new promise to handle the retry
					return new Promise((resolve) => {
						// Exponential backoff delay: 1s, 2s, 4s, etc.
						const delay = Math.pow(2, config.retryCount) * 1000;

						console.log(
							`Retrying request (${config.retryCount}/${MAX_RETRIES}) after ${delay}ms...`
						);

						// Wait for the delay, then retry the request
						setTimeout(() => {
							resolve(axios(config));
						}, delay);
					});
				}
			}

			return Promise.reject(error);
		}
	);

	if (TOKEN) {
		// Set loading state to true
		store.dispatch(setLoading(true));

		// Validate token
		axios
			.get(`/api/me`)
			.then((res) => {
				if (res.data.user?.status !== 'approved') {
					// Account is locked
					clearToken();

					store.dispatch(
						setUser({
							user: null,
						})
					);

					store.dispatch(
						setOrganization({
							organization: null,
						})
					);

					window.location.href = '/login?account=locked';
					return;
				}

				// If user data received successfully, ensure we're using sessionStorage for the token
				if (localStorage.getItem('tokenV3')) {
					setToken(localStorage.getItem('tokenV3'));
				}

				store.dispatch(
					setUser({
						user: res.data.user,
					})
				);

				store.dispatch(
					setOrganization({
						organization: res.data.organization,
					})
				);
				store.dispatch(setLoading(false));
				return TOKEN;
			})
			.catch((err) => {
				// Handle different error cases
				if (err.code === 'ERR_NETWORK' || err.message === 'Network Error') {
					// Network error - don't invalidate token yet
					store.dispatch(
						setError('Network error. Please check your connection.')
					);
					store.dispatch(setLoading(false));
					store.dispatch(setNetworkStatus({ isOnline: false }));

					// We keep the token valid since this is just a network issue
					return;
				}

				// For actual auth errors (401, 403)
				if (
					err.response &&
					(err.response.status === 401 || err.response.status === 403)
				) {
					clearToken();
					store.dispatch(setError('Authentication failed.'));
					window.location.href = '/login?status=authfailed';
					return;
				}

				// For other errors, don't immediately invalidate the session
				store.dispatch(
					setError('Unable to verify authentication. Please try again.')
				);
				store.dispatch(setLoading(false));
				// Don't throw error - handle gracefully to allow error boundary to work
			});
	} else {
		// If no token and it's a public page, set loading to false
		store.dispatch(setLoading(false));
	}
};

// Export token helpers for use in other components
export { getToken, setToken, clearToken };
export default checkAuth;
